import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../lib/utils.ts";

export const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
    ref={ref}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

export const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    className={cn("aspect-square h-full w-full", className)}
    ref={ref}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

export const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className,
    )}
    ref={ref}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export const defaultAvatarSize = "md";

export const avatarVariants = cva("border-2 border-white", {
  variants: {
    size: {
      xs: "size-5",
      sm: "size-6",
      md: "size-7",
      lg: "size-9",
      xl: "size-12",
      "2xl": "size-16",
      "3xl": "size-21",
    },
  },
  defaultVariants: {
    size: defaultAvatarSize,
  },
});

const fallbackVariants = cva("bg-brand-700 text-primary-foreground font-medium", {
  variants: {
    size: {
      xs: "text-2xs",
      sm: "text-2xs",
      md: "text-xs",
      lg: "text-sm",
      xl: "text-base",
      "2xl": "text-lg",
      "3xl": "text-xl",
    },
  },
  defaultVariants: {
    size: defaultAvatarSize,
  },
});

/**
 * Checks if an email address belongs to an Okthx assistant.
 */
export const isOkthxAssistant = (email?: string): boolean => {
  return email === "okthx@okthx.com";
};

/** Composed avatar view, with built in fallback. Generally prefer this over using Avatar directly. */
export const AvatarWithFallback = ({
  size,
  person,
  className,
}: VariantProps<typeof avatarVariants> & {
  className?: string;
  person: { name: string; picture?: string; value: string };
}) => {
  // Use AI avatar for Okthx assistants
  const avatarSrc = isOkthxAssistant(person.value) ? "/ai_avatar.png" : person.picture;
  const initial = (person.name || person.value).slice(0, 1).toUpperCase() || "?";

  return (
    <Avatar className={cn(avatarVariants({ size }), className)}>
      <AvatarImage src={avatarSrc ?? undefined} />
      <AvatarFallback className={cn(fallbackVariants({ size }))}>{initial}</AvatarFallback>
    </Avatar>
  );
};
